import { useState } from "react";
import { Ansicht } from "../design/Ansicht";
import { Dialog } from "../design/Dialog";
import { Ankreuzfeld, Textfeld } from "../design/Formular";
import { Abweisung } from "../design/Hinweis";
import { Knopf } from "../design/Kachel";
import { Symbol } from "../design/Symbol";
import { abweisungsgrund, befehl } from "../plattform/api";
import { useSammlung, type Zeile } from "../plattform/lesemodelle";

/**
 * Hunde im Konto (oberflaeche.md §7 „Hunde“, lesemodelle.md §6): je Hund `article` „<Hundename>“ mit „Ändern“ und
 * „Abmelden“; „Hinzufügen“ öffnet den Dialog „Hund“. Nur mit Konto (LIM-0013).
 */
export function Hunde() {
  const liste = useSammlung("hunde");
  const [dialog, setDialog] = useState<Zeile | "neu" | null>(null);
  const aktiv = [...liste.daten].filter((h) => h["Status"] !== "abgemeldet")
    .sort((a, b) => String(a["Hund"]).localeCompare(String(b["Hund"]), "de"));
  return (
    <Ansicht name="Hunde" symbol="hund" unten={<Knopf art="planung" symbol="hinzufuegen" onPress={() => setDialog("neu")}>Hinzufügen</Knopf>}>
      <section className="liste-bereich platte" role="region" aria-label="Hunde">
        <h2 className="kopf"><Symbol name="hund" />Hunde</h2>
        {aktiv.map((h) => <HundEintrag key={h._id} h={h} onAendern={() => setDialog(h)} />)}
        {aktiv.length === 0 && !liste.laedt ? <div className="leer"><Symbol name="hund" /><span>Noch keine Hunde</span></div> : null}
      </section>
      {dialog ? <HundDialog hund={dialog === "neu" ? null : dialog} onSchliessen={() => setDialog(null)} /> : null}
    </Ansicht>
  );
}

function HundEintrag({ h, onAendern }: { h: Zeile; onAendern: () => void }) {
  const id = `hund-${h._id}`;
  const [grund, setGrund] = useState<string | null>(null);
  async function abmelden() {
    const a = await befehl("HundAbmelden", { Hund: h._id });
    setGrund(abweisungsgrund(a));
  }
  return (
    <article className="zeile" aria-labelledby={id}>
      <Symbol name="hund" />
      <div>
        <div className="zeile__t" id={id}>{String(h["Hund"])}</div>{" "}
        <div className="zeile__u">{String(h["Rasse"])} · {String(h["Farbe"])}</div>
        <div className="zeile__marken"><span className="marke-status">{String(h["Signalhalsung bzw. Warnweste"])}</span>
          {h["Schweißhund"] === true ? <span className="marke-status marke-status--voll">Schweißhund</span> : null}</div>
        <Abweisung grund={grund} />
        <div className="knopfreihe">
          <Knopf art="planung-zweit" symbol="aendern" onPress={onAendern}>Ändern</Knopf>
          <Knopf art="planung-zweit" onPress={abmelden}>Abmelden</Knopf>
        </div>
      </div>
    </article>
  );
}

function HundDialog({ hund, onSchliessen }: { hund: Zeile | null; onSchliessen: () => void }) {
  const [name, setName] = useState(String(hund?.["Hund"] ?? ""));
  const [rasse, setRasse] = useState(String(hund?.["Rasse"] ?? ""));
  const [farbe, setFarbe] = useState(String(hund?.["Farbe"] ?? ""));
  const [kennzeichnung, setKennzeichnung] = useState(String(hund?.["Signalhalsung bzw. Warnweste"] ?? ""));
  const [schweisshund, setSchweisshund] = useState(hund?.["Schweißhund"] === true);
  const [grund, setGrund] = useState<string | null>(null);
  async function speichern() {
    const daten: Record<string, unknown> = {
      Name: name.trim(), Rasse: rasse.trim(), Farbe: farbe.trim(), "Signalhalsung bzw. Warnweste": kennzeichnung.trim(), "Schweißhund": schweisshund,
    };
    const a = hund ? await befehl("HundAendern", { Hund: hund._id, ...daten }) : await befehl("HundAnlegen", daten);
    if (a.ok) onSchliessen(); else setGrund(abweisungsgrund(a));
  }
  return (
    <Dialog name="Hund" symbol="hund" offen onSchliessen={onSchliessen}>
      <div className="formular">
        <Textfeld label="Name" wert={name} onChange={setName} autoFocus />
        <Textfeld label="Rasse" wert={rasse} onChange={setRasse} />
        <Textfeld label="Farbe" wert={farbe} onChange={setFarbe} />
        <Textfeld label="Signalhalsung bzw. Warnweste" wert={kennzeichnung} onChange={setKennzeichnung} />
        <Ankreuzfeld label="Schweißhund" an={schweisshund} onChange={setSchweisshund} />
        <Abweisung grund={grund} />
        <div className="knopfreihe">
          <Knopf onPress={speichern}>Speichern</Knopf>
          <Knopf art="zweit" onPress={onSchliessen}>Abbrechen</Knopf>
        </div>
      </div>
    </Dialog>
  );
}
